import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraCompareColumns, SpecraOverlayDialog } from '@mono/ui-components/captains';
import { SpecraText, SpecraTextarea } from '@mono/ui-components/ensigns';
import { SpecraCombobox, SpecraFormField, SpecraInlineBanner } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useId, useState } from 'react';
import { apiErrorMessage } from '../../../lib/api/apiErrorMessage';
import { useMergeRegistryEntities } from './useMerge';
const COPY = {
    COMPANY: {
        noun: 'company',
        plural: 'companies',
        conflict: 'UEIs'
    },
    GOVERNMENT_ORG: {
        noun: 'government org',
        plural: 'government orgs',
        conflict: 'office codes'
    },
    VEHICLE: {
        noun: 'vehicle',
        plural: 'vehicles',
        conflict: 'contract numbers'
    }
};
const MAX_REASON = 500;
const dash = (v)=>v || '—';
/**
 * Merge {noun} (REG-55). Pick the record to keep, compare, give a reason, merge.
 * Callers mount it only while open, so every session starts from a fresh draft.
 */ export function MergeRecordDialog({ kind, open, onOpenChange, loser, candidates, fieldDefs, onMerged }) {
    const { noun, plural, conflict } = COPY[kind];
    const reasonId = useId();
    const [survivorId, setSurvivorId] = useState();
    const [reason, setReason] = useState('');
    const [error, setError] = useState('');
    const merge = useMergeRegistryEntities(kind);
    const options = candidates.filter((c)=>c.id !== loser.id).map((c)=>({
            value: c.id,
            label: c.reference ? `${c.name} · Reference` : c.name
        }));
    const survivor = candidates.find((c)=>c.id === survivorId);
    const conflicting = !!survivor && fieldDefs.some((f)=>{
        const a = loser.fields[f.id];
        const b = survivor.fields[f.id];
        return f.strong && !!a && !!b && a !== b;
    });
    const trimmed = reason.trim();
    const canMerge = !!survivor && !!trimmed && !conflicting && !merge.isPending;
    const rows = (side, other)=>fieldDefs.map((f)=>{
            const mine = side.fields[f.id];
            const theirs = other?.fields[f.id];
            return {
                id: f.id,
                label: f.label,
                value: dash(mine),
                state: other && (mine || theirs) ? mine === theirs ? 'same' : 'different' : undefined
            };
        });
    const submit = ()=>{
        if (!canMerge || !survivor) return;
        setError('');
        merge.mutate({
            loserId: loser.id,
            survivorId: survivor.id,
            reason: trimmed
        }, {
            onSuccess: (id)=>{
                onOpenChange(false);
                onMerged(id, loser.id);
            },
            onError: (e)=>setError(apiErrorMessage(e, `Could not merge these ${plural}. Try again.`))
        });
    };
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        open: open,
        onOpenChange: (next)=>{
            // A merge in flight must settle before the dialog can go away.
            if (!merge.isPending) onOpenChange(next);
        },
        size: "lg",
        title: `Merge ${noun}`,
        testId: Testids.registryMerge.dialog,
        footerProps: {
            confirmLabel: 'Merge',
            loadingLabel: 'Merging…',
            loading: merge.isPending,
            confirmDisabled: !canMerge,
            onConfirm: submit,
            onCancel: ()=>onOpenChange(false),
            confirmProps: {
                variant: 'destructive',
                'data-testid': Testids.registryMerge.confirm
            },
            cancelProps: {
                disabled: merge.isPending,
                'data-testid': Testids.registryMerge.cancel
            }
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-[var(--space-4)]",
            children: [
                /*#__PURE__*/ _jsx(SpecraCombobox, {
                    label: "Keep",
                    placeholder: `Pick the ${noun} to keep`,
                    searchPlaceholder: `Search ${plural}`,
                    emptyMessage: `No other ${plural}`,
                    options: options,
                    value: survivorId,
                    onValueChange: setSurvivorId,
                    disabled: merge.isPending,
                    "data-testid": Testids.registryMerge.survivorPicker
                }),
                survivor && /*#__PURE__*/ _jsxs("div", {
                    "data-testid": Testids.registryMerge.compare,
                    children: [
                        /*#__PURE__*/ _jsx(SpecraCompareColumns, {
                            framed: "cards",
                            sides: [
                                {
                                    id: 'loser',
                                    eyebrow: 'Will be merged',
                                    title: loser.name,
                                    rows: rows(loser, survivor)
                                },
                                {
                                    id: 'survivor',
                                    eyebrow: 'Will keep',
                                    title: survivor.name,
                                    rows: rows(survivor, loser)
                                }
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(SpecraText, {
                            as: "p",
                            size: "meta",
                            tone: "muted",
                            className: "mt-[var(--space-2)]",
                            children: [
                                "Nothing is deleted. Links, notes and history on ",
                                loser.name,
                                " now show on",
                                ' ',
                                survivor.name,
                                ". You can undo this from the merge history."
                            ]
                        }),
                        kind === 'COMPANY' && /*#__PURE__*/ _jsxs(SpecraText, {
                            as: "p",
                            size: "meta",
                            tone: "muted",
                            className: "mt-[var(--space-1)]",
                            children: [
                                "Comp-vault access stays per company. People granted ",
                                survivor.name,
                                " do not gain",
                                ' ',
                                loser.name,
                                "'s person-level rates."
                            ]
                        })
                    ]
                }),
                conflicting && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                    tone: "danger",
                    "data-testid": Testids.registryMerge.conflict,
                    children: [
                        "These records carry different ",
                        conflict,
                        ". They are different ",
                        plural,
                        "."
                    ]
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Reason",
                    htmlFor: reasonId,
                    required: true,
                    hint: `${reason.length}/${MAX_REASON}`,
                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                        id: reasonId,
                        value: reason,
                        maxLength: MAX_REASON,
                        disabled: merge.isPending,
                        onChange: (e)=>setReason(e.target.value),
                        "data-testid": Testids.registryMerge.reason
                    })
                }),
                error && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "danger",
                    "data-testid": Testids.registryMerge.error,
                    children: error
                })
            ]
        })
    });
}
